/**
 * TaskFromSuggestionModal Component
 * Modale per creare una task da un suggerimento dell'agent AI
 * Supporta allegati da WhatsApp e upload da PC
 */

import { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ListTodo, Loader2, Paperclip, Image, Upload } from 'lucide-react';
import { useProjectStore } from '@/lib/stores/projectStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useAuth } from '@/lib/auth';
import { WhatsAppImagePicker } from '../chat/WhatsAppImagePicker';
import type { AgentRequest } from '@/lib/types/whatsappAgent';

interface TaskFromSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: AgentRequest;
  projectId: string | null;
  projectName: string | null;
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa', color: 'text-gray-500' },
  { value: 'medium', label: 'Media', color: 'text-blue-500' },
  { value: 'high', label: 'Alta', color: 'text-orange-500' },
  { value: 'urgent', label: 'Urgente', color: 'text-red-500' },
];

const PRIORITY_MAP: Record<string, string> = {
  bug_report: 'high',
  support_request: 'medium',
  feature_request: 'medium',
  task_request: 'medium',
  status_inquiry: 'low',
};

export function TaskFromSuggestionModal({
  isOpen,
  onClose,
  request,
  projectId,
  projectName,
}: TaskFromSuggestionModalProps) {
  const { user } = useAuth();
  const { createTask, uploadTaskAttachment } = useProjectStore();
  const { updateRequestStatus } = useWhatsAppAgentStore();
  const { selectedChatId } = useWhatsAppChatStore();

  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(
    `${request.description}\n\n---\nMessaggio originale: ${request.original_text}\nMittente: ${request.sender_name || 'Sconosciuto'}`
  );
  const [priority, setPriority] = useState(PRIORITY_MAP[request.request_type] || 'medium');
  const [saving, setSaving] = useState(false);

  // Attachments state
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFiles = useCallback((files: File[]) => {
    setAttachments(prev => [...prev, ...files]);
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      handleAddFiles(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleAddFiles]);

  const handleCreate = useCallback(async () => {
    if (!projectId || !title.trim()) return;
    setSaving(true);
    try {
      const task = await createTask({
        project_id: projectId,
        title: title.trim(),
        description: description.trim(),
        status: 'todo',
        priority,
        section: 'backlog',
        is_client_request: true,
        created_by: user?.id,
      });

      if (task) {
        // Upload allegati
        if (attachments.length > 0) {
          for (const file of attachments) {
            try {
              await uploadTaskAttachment(task.id, file);
            } catch (err) {
              console.error('[TaskFromSuggestionModal] Error uploading attachment:', err);
            }
          }
        }

        // Update request status and link task
        await supabaseUpdateRequest(request.id, task.id, user?.id);
        // Remove from local suggestions
        useWhatsAppAgentStore.setState((state) => ({
          chatSuggestions: state.chatSuggestions.filter((s) => s.id !== request.id),
        }));
        onClose();
      }
    } catch (err) {
      console.error('[TaskFromSuggestionModal] Error creating task:', err);
    } finally {
      setSaving(false);
    }
  }, [projectId, title, description, priority, user, request, attachments, createTask, uploadTaskAttachment, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Crea task da suggerimento</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Project info */}
          {projectName && (
            <div className="text-xs text-[#787774]">
              Progetto: <span className="font-medium text-[#37352F] dark:text-white">{projectName}</span>
            </div>
          )}
          {!projectId && (
            <div className="text-xs text-red-500">
              Nessun progetto associato a questa chat. Associa un progetto dal pannello Agent AI.
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titolo
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrizione
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priorità
            </label>
            <div className="flex gap-2">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setPriority(opt.value)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    priority === opt.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Allegati
            </label>

            {/* Attachment buttons */}
            <div className="flex gap-2 mb-2">
              {selectedChatId && (
                <button
                  onClick={() => setShowImagePicker(prev => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                    showImagePicker
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Image className="w-3.5 h-3.5" />
                  Da WhatsApp
                </button>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Da PC
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>

            {/* WhatsApp image picker */}
            {showImagePicker && selectedChatId && (
              <div className="mb-2">
                <WhatsAppImagePicker
                  chatId={selectedChatId}
                  onSelect={(files) => {
                    handleAddFiles(files);
                    setShowImagePicker(false);
                  }}
                  onClose={() => setShowImagePicker(false)}
                />
              </div>
            )}

            {/* Attachment list */}
            {attachments.length > 0 && (
              <div className="space-y-1">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[#F7F7F5] dark:bg-gray-700"
                  >
                    <Paperclip className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" />
                    <span className="text-xs text-[#37352F] dark:text-white truncate flex-1">
                      {file.name}
                    </span>
                    <span className="text-[10px] text-[#787774] flex-shrink-0">
                      {(file.size / 1024).toFixed(0)}KB
                    </span>
                    <button
                      onClick={() => handleRemoveAttachment(index)}
                      className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10"
                    >
                      <X className="w-3 h-3 text-[#787774]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !projectId || !title.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Crea task{attachments.length > 0 ? ` (${attachments.length} allegat${attachments.length === 1 ? 'o' : 'i'})` : ''}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Helper to update request in DB after task creation
async function supabaseUpdateRequest(requestId: string, taskId: string, userId?: string) {
  const { supabase } = await import('@/lib/supabase');
  await supabase
    .from('whatsapp_agent_requests')
    .update({
      status: 'task_created',
      created_task_id: taskId,
      reviewed_by: userId || null,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', requestId);
}
